import Form from "common/Form/Form";
import Input from "common/Inputs/Input";
import { useHistory } from "react-router-dom";
import { MOBILE_SEARCH, SECURITY_SETTINGS_PATH } from "common/constants";
import Flex from "common/Boxes/Flex";
import useMedia from "utils/hooks/useMedia";
import Button from "common/Buttons/Button";
import { ConfigurationCard } from "features/settings/common/styles";
import { sharedLib } from "@shared/services/shared.service";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { displayResultMessage } from "utils/misc.tsx";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import React from "react";
import Title from "common/Typography/Title.tsx";
import styled, { useTheme } from "styled-components";
import { DarkModeUser, User } from "common/Icons/Icons.tsx";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import NewLookFormPanel, {
  NewLookFormActions,
} from "features/settings/NewLook/NewLookFormPanel.tsx";

const ChangeNamePage = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { user } = useUser();
  const [setPersonalDetails, setPersonalDetailsStatus] =
    sharedLib.sharedApi.authentication.useSetPersonalDetailsMutation();

  const saveChanges = (formData: FormData) => {
    const changeNameRequest = {
      first_name: formData.firstName,
      last_name: formData.lastName,
    };
    displayResultMessage(
      setPersonalDetails(changeNameRequest),
      t("change_name.success_message"),
      t("change_name.error_message")
    )
      .catch(() => {})
      .then(() => history.replace(SECURITY_SETTINGS_PATH));
  };

  const UserIcon = theme.isDarkMode ? DarkModeUser : User;
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const nameItems = (
    <>
      <Form.Item
        label={t("change_name.first_name")}
        name={FormItem.FIRST_NAME}
        initialValue={user?.first_name}
        rules={[
          {
            required: true,
            message: t("change_name.enter_first_name"),
          },
        ]}
      >
        <Input placeholder={t("change_name.first_name_placeholder")} />
      </Form.Item>
      <Form.Item
        label={t("change_name.last_name")}
        name={FormItem.LAST_NAME}
        initialValue={user?.last_name}
        rules={[
          {
            required: true,
            message: t("change_name.enter_last_name"),
          },
        ]}
      >
        <Input placeholder={t("change_name.last_name_placeholder")} />
      </Form.Item>
    </>
  );

  if (theme.isNewLook) {
    return (
      <NewLookFormPanel
        title={t("change_name.title")}
        onBack={() => history.goBack()}
        backLabel={tCommon("page_title.account_and_security")}
      >
        <Form layout="vertical" onFinish={saveChanges}>
          <NameFields>{nameItems}</NameFields>
          <NewLookFormActions>
            <Button
              type="primary"
              htmlType="submit"
              loading={setPersonalDetailsStatus.isLoading}
            >
              {t("change_name.update")}
            </Button>
          </NewLookFormActions>
        </Form>
      </NewLookFormPanel>
    );
  }

  return (
    <Flex
      $justifyContent="center"
      $alignItems="center"
      style={{ marginTop: 32 }}
    >
      <div>
        <BackWrapper>
          <ArrowLeft onClick={() => history.goBack()} />
          <Title level={3} style={{ marginLeft: 12 }}>
            {t("change_name.title")}
          </Title>
        </BackWrapper>
        <ConfigurationCard style={!isMobile ? { width: 375 } : {}}>
          <UserIcon
            style={{
              display: "block",
              width: 125,
              height: "auto",
              margin: "0 auto 16px auto",
            }}
          />
          <Form layout="vertical" onFinish={saveChanges}>
            {nameItems}
            <Button
              type="primary"
              htmlType="submit"
              style={{ marginTop: 8, width: "100%" }}
              loading={setPersonalDetailsStatus.isLoading}
            >
              {t("change_name.update")}
            </Button>
          </Form>
        </ConfigurationCard>
      </div>
    </Flex>
  );
};

export default ChangeNamePage;

enum FormItem {
  FIRST_NAME = "firstName",
  LAST_NAME = "lastName",
}
type FormData = { [key in FormItem]: string };

const BackWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;
const ArrowLeft = styled(ArrowLeftOutlined)`
  font-size: 18px;
  cursor: pointer;
`;
/** First and last name side by side (new look, stacked on phones). */
const NameFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;
  @media (max-width: 500px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
